import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { errorMessage, isVersionConflict } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import { formatDateTime } from "../lib/missions";
import {
  VERSION_STATUS_LABELS, audienceLabel, formatMinutes, moveEntry, programDraftProblem, programSummary, programTotals, trainingError,
} from "../lib/training";
import useIsMobile from "../hooks/useIsMobile";
import useJamoraCatalog from "../hooks/useJamoraCatalog";
import { useBottomBar } from "../hooks/useCopilotDock";
import { AddModuleDialog, AudienceFields, ProgramPreview } from "../components/training/ProgramBits";
import { StatePill } from "../components/CollaboratorBits";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  ResponsiveDropdownMenu as DropdownMenu, ResponsiveDropdownMenuContent as DropdownMenuContent, ResponsiveDropdownMenuItem as DropdownMenuItem,
  ResponsiveDropdownMenuTrigger as DropdownMenuTrigger,
} from "../components/ui/responsive-dropdown-menu";
import { toast } from "sonner";
import { AlertTriangle, ArrowDown, ArrowLeft, ArrowUp, Eye, Loader2, MoreHorizontal, Pencil, Plus, Trash2 } from "lucide-react";

const HISTORY_LABELS = {
  created: "Programme créé", updated: "Programme modifié", draft_created: "Nouvelle version commencée", draft_saved: "Brouillon enregistré",
  draft_discarded: "Brouillon supprimé", published: "Version publiée",
};

const entriesOf = (version) => (version?.items || []).map((i) => ({ module_id: i.module_id, required: !!i.required }));

// A draft's preview shows the modules as they are now (unsaved order and choices included); a published version shows its frozen copy.
function draftItems(entries, modulesById, savedItems) {
  const fromVersion = new Map((savedItems || []).map((i) => [i.module_id, i]));
  return entries.map((entry) => {
    const module = modulesById[entry.module_id];
    if (module) return { ...module, module_id: module.id, required: entry.required, module_active: module.active };
    return { ...(fromVersion.get(entry.module_id) || { title: "Module introuvable", objectives: [], missing: true }), module_id: entry.module_id, required: entry.required };
  });
}

export default function TrainingProgramEditor() {
  const { id } = useParams();
  const mobile = useIsMobile();
  const catalog = useJamoraCatalog(true);
  const [program, setProgram] = useState(null);
  const [phase, setPhase] = useState("loading"); // loading | ready | missing | error
  const [errorText, setErrorText] = useState("");
  const [versionId, setVersionId] = useState(null);
  const [version, setVersion] = useState(null);
  const [entries, setEntries] = useState([]);
  const [saved, setSaved] = useState("[]");
  const [modules, setModules] = useState([]);
  const [view, setView] = useState("edit"); // edit | preview (phone)
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null); // add | publish | discard | details | toggle
  const [deleting, setDeleting] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const loadProgram = useCallback(async (preferred) => {
    try {
      const { data } = await api.get(`/training/programs/${id}`);
      setProgram(data);
      setPhase("ready");
      setVersionId(preferred || data.draft_version?.id || data.current_version?.id || data.versions[0]?.id || null);
      return data;
    } catch (e) {
      if (e?.response?.status === 404) setPhase("missing");
      else { setErrorText(trainingError(e, "Impossible de charger le programme.")); setPhase("error"); }
      return null;
    }
  }, [id]);
  useEffect(() => { loadProgram(); }, [loadProgram]);

  const loadModules = useCallback(async () => {
    try {
      const { data } = await api.get("/training/modules");
      setModules(data.items);
    } catch (e) {
      toast.error(trainingError(e, "Impossible de charger les modules."));
    }
  }, []);
  useEffect(() => { if (program?.can_manage) loadModules(); }, [program?.can_manage, loadModules]);

  const loadVersion = useCallback(async (vid) => {
    if (!vid) { setVersion(null); setEntries([]); setSaved("[]"); return; }
    try {
      const { data } = await api.get(`/training/programs/${id}/versions/${vid}`);
      setVersion(data);
      setEntries(entriesOf(data));
      setSaved(JSON.stringify(entriesOf(data)));
    } catch (e) {
      toast.error(trainingError(e, "Impossible de charger cette version."));
    }
  }, [id]);
  useEffect(() => { loadVersion(versionId); }, [versionId, loadVersion]);

  const isDraft = version?.status === "draft";
  const dirty = isDraft && JSON.stringify(entries) !== saved;
  useBottomBar(dirty);
  const modulesById = useMemo(() => Object.fromEntries(modules.map((m) => [m.id, m])), [modules]);
  const items = useMemo(() => (isDraft ? draftItems(entries, modulesById, version?.items) : version?.items || []), [isDraft, entries, modulesById, version]);
  const problem = useMemo(() => (isDraft ? programDraftProblem(entries, modulesById) : null), [isDraft, entries, modulesById]);
  const publishProblem = useMemo(() => (isDraft ? programDraftProblem(entries, modulesById, { publishing: true }) : null), [isDraft, entries, modulesById]);

  const run = async (request, success) => {
    setBusy(true);
    try {
      const { data } = await request();
      toast.success(success);
      return data;
    } catch (e) {
      toast.error(trainingError(e));
      if (isVersionConflict(e)) { await loadProgram(versionId); await loadVersion(versionId); }
      return null;
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    const data = await run(() => api.put(`/training/programs/${id}/versions/${version.id}`, { expected_version: version.version, modules: entries }),
      "Brouillon enregistré");
    if (data) { setVersion(data); setEntries(entriesOf(data)); setSaved(JSON.stringify(entriesOf(data))); }
  };
  const publish = async () => {
    const data = await run(() => api.post(`/training/programs/${id}/versions/${version.id}/publish`, { expected_version: version.version }),
      `Version ${version.number} publiée`);
    // Same version id before and after: the effect that loads a version would not run again, so take the server's answer.
    if (data) { setDialog(null); setVersion(data); setEntries(entriesOf(data)); setSaved(JSON.stringify(entriesOf(data))); await loadProgram(data.id); }
  };
  const startVersion = async () => {
    const data = await run(() => api.post(`/training/programs/${id}/versions`), "Nouvelle version commencée");
    if (data) await loadProgram(data.id);
  };
  const discard = async () => {
    const data = await run(() => api.post(`/training/programs/${id}/versions/${version.id}/discard`, { expected_version: version.version }), "Brouillon supprimé");
    if (data) { setDialog(null); await loadProgram(data.current_version?.id || null); }
  };

  if (phase === "loading") return <div className="space-y-3"><Skeleton className="h-8 w-72" /><Skeleton className="h-64 w-full" /></div>;
  if (phase === "missing" || phase === "error") {
    return (
      <Card className="p-8 text-center space-y-3 max-w-xl mx-auto" data-testid="training-program-missing">
        <AlertTriangle className="mx-auto text-slate-300" />
        <p className="text-sm text-slate-700">{phase === "missing" ? "Ce programme n'existe pas ou vous n'y avez pas accès." : errorText}</p>
        <Button variant="outline" asChild><Link to="/formations">Retour aux formations</Link></Button>
      </Card>
    );
  }

  const manager = program.can_manage;
  const editor = isDraft && manager && (
    <div className="space-y-3" data-testid="program-editor">
      {entries.length === 0 && (
        <Card className="p-6 text-center text-sm text-slate-500">Ajoutez les modules dans l'ordre où ils seront animés.</Card>
      )}
      {entries.map((entry, index) => {
        const item = items[index];
        return (
          <Card key={entry.module_id} className={`p-3 flex items-center gap-2 ${item?.missing || item?.module_active === false ? "border-amber-300" : ""}`}
            data-testid={`program-entry-${index}`}>
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">{index + 1}</span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-slate-900 break-words">{item?.title}</p>
              <p className="text-xs text-slate-500">
                {item?.missing ? "Module introuvable" : item?.module_active === false ? "Module désactivé"
                  : [`${(item?.objectives || []).length} objectif${(item?.objectives || []).length > 1 ? "s" : ""}`, formatMinutes(item?.duration_minutes)].filter(Boolean).join(" · ")}
              </p>
            </div>
            <label className="flex items-center gap-1.5 text-xs text-slate-600 shrink-0 cursor-pointer">
              <Switch checked={entry.required} aria-label="Module obligatoire" data-testid={`program-entry-required-${index}`}
                onCheckedChange={(value) => setEntries((all) => all.map((e) => (e.module_id === entry.module_id ? { ...e, required: value } : e)))} />
              <span className="hidden sm:inline">{entry.required ? "Obligatoire" : "Facultatif"}</span>
            </label>
            <Button type="button" variant="ghost" size="icon" aria-label="Monter le module" disabled={index === 0}
              onClick={() => setEntries((all) => moveEntry(all, index, -1))}><ArrowUp size={14} /></Button>
            <Button type="button" variant="ghost" size="icon" aria-label="Descendre le module" disabled={index === entries.length - 1}
              onClick={() => setEntries((all) => moveEntry(all, index, 1))}><ArrowDown size={14} /></Button>
            <Button type="button" variant="ghost" size="icon" aria-label="Retirer le module" className="text-slate-500 hover:text-red-700"
              onClick={() => setEntries((all) => all.filter((e) => e.module_id !== entry.module_id))} data-testid={`program-entry-remove-${index}`}><Trash2 size={14} /></Button>
          </Card>
        );
      })}
      <Button type="button" variant="outline" onClick={() => setDialog("add")} data-testid="program-add-module"><Plus size={16} className="mr-1.5" />Ajouter un module</Button>
    </div>
  );

  return (
    <div className="space-y-4 pb-24" data-testid="training-program-page">
      <DeleteWithReasonDialog
        target={deleting} testId="delete-program" onClose={() => setDeleting(null)}
        title={`Supprimer « ${deleting?.label || ""} » ?`}
        description="Ce programme n'a jamais été publié ni utilisé par une session. Il va dans la corbeille : vous pourrez le restaurer pendant 30 jours."
        onConfirm={async (reason) => {
          try {
            await api.delete(`/training/programs/${id}`, { data: { reason } });
            toast.success("Programme mis à la corbeille");
            navigate("/formations");
          } catch (e) {
            toast.error(errorMessage(e, "Impossible de supprimer le programme."));
          } finally {
            setDeleting(null);
          }
        }}
      />
      <Link to="/formations" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ArrowLeft size={14} /> Retour aux formations</Link>
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-display font-bold text-2xl text-slate-900 break-words" data-testid="training-program-title">{program.label}</h1>
            {manager && <StatePill active={program.active} activeLabel="Actif" inactiveLabel="Désactivé" />}
          </div>
          <p className="text-sm text-slate-500" data-testid="training-program-audience">Pour : {audienceLabel(program, catalog.roles)}</p>
          {program.description && <p className="text-sm text-slate-500 whitespace-pre-line">{program.description}</p>}
        </div>
        {manager && (
          <div className="flex items-center gap-2 shrink-0">
            {isDraft && (
              <Button onClick={() => setDialog("publish")} disabled={busy || dirty} data-testid="program-publish"
                title={dirty ? "Enregistrez d'abord vos modifications" : undefined}>Publier la version {version.number}</Button>
            )}
            {!program.draft_version && <Button variant="outline" onClick={startVersion} disabled={busy} data-testid="program-new-version">Nouvelle version</Button>}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" aria-label="Autres actions" data-testid="program-more"><MoreHorizontal size={16} /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" title="Programme">
                <DropdownMenuItem onClick={() => setDialog("details")} data-testid="program-edit-details"><Pencil size={14} className="mr-2" />Nom, description et public</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setDialog("toggle")} data-testid="program-toggle">{program.active ? "Désactiver" : "Réactiver"}</DropdownMenuItem>
                {isDraft && <DropdownMenuItem onClick={() => setDialog("discard")} className="text-red-700" data-testid="program-discard">Supprimer le brouillon</DropdownMenuItem>}
                {hasPermission(user, "training:delete") && <DropdownMenuItem onClick={() => setDeleting(program)} className="text-red-700" data-testid="program-delete">Supprimer le programme…</DropdownMenuItem>}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      {program.versions.length > 1 || manager ? (
        <div className="flex gap-1 overflow-x-auto" role="tablist" aria-label="Versions" data-testid="program-versions">
          {program.versions.map((v) => (
            <button key={v.id} type="button" role="tab" aria-selected={v.id === versionId} onClick={() => !dirty && setVersionId(v.id)} disabled={dirty && v.id !== versionId}
              data-testid={`program-version-${v.number}`}
              className={`shrink-0 rounded-full px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 ${v.id === versionId ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
              v{v.number} · {VERSION_STATUS_LABELS[v.status]}
            </button>
          ))}
        </div>
      ) : null}

      {!version ? (
        <Card className="p-6 text-sm text-slate-500">Aucune version. Commencez-en une avec « Nouvelle version ».</Card>
      ) : (
        <>
          <p className="text-sm text-slate-500" data-testid="program-version-summary">
            {isDraft ? "Brouillon, modifiable" : version.status === "published" ? `Version ${version.number}, publiée le ${formatDateTime(version.published_at)}`
              : `Remplacée le ${formatDateTime(version.deprecated_at)}, gardée pour les sessions déjà faites`}
            {" · "}{programSummary(programTotals(items))}
          </p>
          {isDraft && manager && mobile && (
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" role="tablist">
              {[["edit", "Modifier", Pencil], ["preview", "Aperçu", Eye]].map(([key, label, Icon]) => (
                <button key={key} type="button" role="tab" aria-selected={view === key} onClick={() => setView(key)} data-testid={`program-view-${key}`}
                  className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm ${view === key ? "bg-jamora text-white" : "text-slate-600"}`}><Icon size={14} />{label}</button>
              ))}
            </div>
          )}
          {isDraft && manager ? (
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_420px] items-start">
              {(!mobile || view === "edit") && editor}
              {(!mobile || view === "preview") && <div className="lg:sticky lg:top-24"><ProgramPreview items={items} /></div>}
            </div>
          ) : (
            <div className="max-w-3xl"><ProgramPreview items={items} /></div>
          )}
        </>
      )}

      {program.history?.length > 0 && (
        <details className="rounded-lg border border-slate-200 bg-white p-4" data-testid="program-history">
          <summary className="cursor-pointer text-sm font-medium text-slate-900">Historique</summary>
          <ol className="mt-3 space-y-2">
            {program.history.map((h, i) => (
              <li key={i} className="text-sm">
                <span className="text-slate-800">{HISTORY_LABELS[h.kind] || h.kind}{h.data?.number ? ` (v${h.data.number})` : ""}</span>
                <span className="text-xs text-slate-500"> · {formatDateTime(h.at)} · {h.actor_name}</span>
              </li>
            ))}
          </ol>
        </details>
      )}

      {dirty && (
        <div className="fixed inset-x-0 bottom-[4.75rem] lg:bottom-4 z-30 px-4 lg:left-64" data-testid="program-save-bar">
          <div className="mx-auto max-w-4xl rounded-xl border border-slate-200 bg-white p-3 shadow-lg flex items-center justify-between gap-2">
            <span className="text-xs text-slate-500 min-w-0">{problem ? <span className="text-amber-800">{problem}</span> : "Modifications non enregistrées"}</span>
            <div className="flex gap-2 shrink-0">
              <Button variant="ghost" size="sm" onClick={() => setEntries(JSON.parse(saved))}>Annuler</Button>
              <Button size="sm" onClick={save} disabled={busy || !!problem} data-testid="program-save">
                {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Enregistrer
              </Button>
            </div>
          </div>
        </div>
      )}

      {manager && (
        <AddModuleDialog open={dialog === "add"} onClose={() => setDialog(null)} modules={modules} excluded={entries.map((e) => e.module_id)}
          onAdd={(ids) => setEntries((all) => [...all, ...ids.map((moduleId) => ({ module_id: moduleId, required: true }))])} />
      )}

      <Dialog open={dialog === "publish"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent data-testid="program-publish-dialog">
          <DialogHeader>
            <DialogTitle>Publier la version {version?.number} ?</DialogTitle>
            <DialogDescription>
              Les sessions préparées à partir de maintenant utiliseront cette version. Elle garde une copie des modules : les modifier ensuite ne la change plus.
            </DialogDescription>
          </DialogHeader>
          {publishProblem && <p className="text-sm text-amber-800" role="alert">{publishProblem}</p>}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDialog(null)} disabled={busy}>Annuler</Button>
            <Button onClick={publish} disabled={busy || !!publishProblem} data-testid="program-publish-confirm">
              {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Publier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "discard"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer le brouillon v{version?.number} ?</DialogTitle>
            <DialogDescription>Il n'a jamais été publié : rien d'autre ne change. Les versions publiées restent.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDialog(null)} disabled={busy}>Annuler</Button>
            <Button onClick={discard} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="program-discard-confirm">Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {manager && (
        <DetailsDialog open={dialog === "details" || dialog === "toggle"} mode={dialog} program={program} roles={catalog.roles} onClose={() => setDialog(null)}
          onDone={(data) => { setDialog(null); setProgram((p) => ({ ...p, ...data })); }} />
      )}
    </div>
  );
}

function DetailsDialog({ open, mode, program, roles, onClose, onDone }) {
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [audience, setAudience] = useState({ kind: "pharmacy", audience: [] });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setLabel(program.label);
      setDescription(program.description || "");
      setAudience({ kind: program.audience_kind || "pharmacy", audience: program.audience || [] });
    }
  }, [open, program]);
  const toggle = mode === "toggle";
  const submit = async () => {
    setBusy(true);
    try {
      const body = toggle ? { expected_version: program.version, active: !program.active }
        : { expected_version: program.version, label: label.trim(), description: description.trim() || null, audience_kind: audience.kind, audience: audience.audience };
      const { data } = await api.patch(`/training/programs/${program.id}`, body);
      toast.success(toggle ? (program.active ? "Programme désactivé" : "Programme réactivé") : "Programme mis à jour");
      onDone(data);
    } catch (e) {
      toast.error(trainingError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="program-details-dialog">
        <DialogHeader>
          <DialogTitle>{toggle ? (program.active ? `Désactiver « ${program.label} » ?` : `Réactiver « ${program.label} » ?`) : "Nom, description et public"}</DialogTitle>
          {toggle && (
            <DialogDescription>
              {program.active ? "Il ne sera plus proposé pour de nouvelles sessions. Ce qui a déjà été fait reste consultable." : "Il pourra de nouveau être utilisé."}
            </DialogDescription>
          )}
        </DialogHeader>
        {!toggle && (
          <div className="space-y-4">
            <div className="space-y-1.5"><Label htmlFor="tp-label">Nom *</Label>
              <Input id="tp-label" maxLength={160} className="text-base md:text-sm" value={label} onChange={(e) => setLabel(e.target.value)} /></div>
            <div className="space-y-1.5"><Label htmlFor="tp-desc">Description</Label>
              <Textarea id="tp-desc" rows={2} maxLength={4000} className="text-base md:text-sm" value={description} onChange={(e) => setDescription(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Public visé</Label>
              <AudienceFields kind={audience.kind} audience={audience.audience} roles={roles} onChange={setAudience} idPrefix="program-edit-audience" /></div>
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button onClick={submit} disabled={busy || (!toggle && label.trim().length < 2)} data-testid="program-details-confirm">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{toggle ? (program.active ? "Désactiver" : "Réactiver") : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
